import EventEmitter from 'eventemitter3'
import { NetLoader, StreamingError, EVENT, Logger, ERR } from 'xgplayer-streaming-shared'
import { EXTEND_EVENTS, RTC_FIRST_FRAME_STATE } from './constants'
import SdpTransform from 'sdp-transform'
import { getOption, IAdaptiveJitterBufferOption, INetworkStrategy, IPreloadPromise, RtsOption } from './options'
import { getStats, getStatsSnapshoot } from './getStats'
import { isEncodedTransformSupported, isLegacyEncodedTransformSupported } from './isSupported'
import { receiveSEITransform } from './sei-transform/receive-sei.worker'
import ReceivedSEIWorker from './sei-transform/receive-sei.worker?worker&inline'
import AudioStallObserver from './audioStall'
import VideoStallObserver from './videoStall'
import { diffRecvAudioStats, diffRecvVideoStats, getRecvAudioStats, getRecvVideoStats, IRecvAudioStats, IRecvVideoStats } from './statsProcess'
import NetworkQuality from './networkQuality'
import { FirstFrameObserver, FirstFrameObserverExtraInfo, FirstFrameStateResult } from './firstFrameObserver'
import { addUrlParams, compareURL } from './utils'

const logger = new Logger('rts')

export { logger }

declare global {
  // TypeScript 中缺少此定义
  interface RTCConfiguration {
    sdpSemantics?: 'plan-b' | 'unified-plan';
    encodedInsertableStreams?: boolean;
  }
}

export default class Rts extends EventEmitter {
  private _url = ''

  private _pc: RTCPeerConnection | null = null

  private _audioTransceicer: RTCRtpTransceiver | null = null

  private _videoTransceicer: RTCRtpTransceiver | null = null

  private _mediaStream: MediaStream | null = null

  private _media: HTMLVideoElement | null

  private _opts: RtsOption

  private _loader: NetLoader | null = null

  private _retry = 0

  private _waitingTimer = 0

  private _rctConnectStartTs = 0

  private _firstFrameObserver = new FirstFrameObserver(this._emitFirstFrameState.bind(this))

  private _networkQualityResult: (number | undefined)[] = []

  private _audio: MediaStreamTrack | null = null

  private _video: MediaStreamTrack | null = null

  private _checkStatsErrorTimer?: number

  private _audioStallObserver?: AudioStallObserver

  private _videoStallObserver?: VideoStallObserver

  private _networkQuality?: NetworkQuality
  
  private _preAudioStats: IRecvAudioStats | null = null

  private _preVideoStats: IRecvVideoStats | null = null

  private _rtcReportTimer: number | null = null

  private _preloadPromise?: IPreloadPromise

  private _videoJitterBufferDelay = 0

  static enableLogger () {
    Logger.enable()
  }

  static disableLogger () {
    Logger.disable()
  }

  get pc () {
    return this._pc
  }

  get audioTrack () {
    return this._audio
  }

  get videoTack () {
    return this._video
  }

  public getJitterBufferDelay () {
    return this._videoJitterBufferDelay
  }

  get loader () {
    return this._loader
  }

  public async getStats () {
    return getStats(this._pc)
  }

  public getStatsSnapshoot () {
    return this.getStats().then(stats => {
      return getStatsSnapshoot(stats)
    })
  }

  constructor (opts: RtsOption) {
    super()
    this._opts = getOption(opts)
    this._media = this._opts.media
    this._loader = new NetLoader({
      ...opts.fetchOptions,
      responseType: 'json',
      method: 'POST',
      retry: this._opts.retryCount,
      retryDelay: this._opts.retryDelay,
      timeout: this._opts.loadTimeout,
      onRetryError: this._onLoaderRetry,
      logger
    })
    this._retry = this._opts.retryCount || 0
    this._preloadPromise = this._opts.preloadPromise
    this._bindMediaEvent()
    logger.log('constructor()', opts)
  }

  async load (url: string) {
    logger.log('load()', `invoked, url: "${url}"`)
    this._disconnect()

    this._url = url
    this._retry = this._opts.retryCount || 0

    const lastPc = this._pc

    try {
      this._firstFrameObserver.start()
      let answerSdp: string | undefined
      // 如果存在预加载，且加载地址相同
      if (this._preloadPromise) {
        logger.log('load()', 'in preload process...')
        try {
          this._firstFrameObserver.setIsPreload(true)
          if (!compareURL(this._url, this._preloadPromise.url, ['tabr_start_bitrate'])) {
            const errorStr = `preload url not match, current url: ${this._url}, preload url: ${this._preloadPromise.url}`;
            delete this._preloadPromise
            throw new Error(errorStr)
          }
          this._loader && (this._loader.finnalUrl = this._preloadPromise.url)
          const preloadPromise = this._preloadPromise.promise
          delete this._preloadPromise
          const info = await preloadPromise
          if (info.pc.signalingState !== 'have-local-offer') {
            throw new Error(`pc.signalingState not equal "have-local-offer", current state is ${info.pc.signalingState}`)
          }
          this._pc = info.pc
          answerSdp = info.answerSdp
          this._videoTransceicer = info.videoTransceiver
          this._audioTransceicer = info.audioTransceiver
          if (this._opts.enableSei) {
            this.initVideoEncodedTransform()
            this.initAudioEncodedTransform()
          }
        } catch (e: any) {
          logger.warn('load()', `preload failed, ${e.message || e.toString()}`)
          this._firstFrameObserver.setFailureReason(e.message || e.toString())
          // 如果预加载失败，重新加载
          this.load(url)
          return
        }
      } else {
        const encodedTransformSupported = isLegacyEncodedTransformSupported()
        logger.log('load()', `isLegacyEncodedTransformSupported: ${encodedTransformSupported}`)
        this._pc = new RTCPeerConnection({
          encodedInsertableStreams: encodedTransformSupported && this._opts.enableSei ? true : undefined
        })
        answerSdp = await this._getAnswerSdp(this._pc)
      }

      this._bindRTCEvents(this._pc)
      await this._handleAnswerSdp(this._pc, answerSdp)

      if (lastPc) {
        lastPc.close()
      }
    } catch (e: any) {
      setTimeout(() => this._emitError(StreamingError.create(ERR.OTHER, null, e)))
    }
  }

  switchURL (url?: string) {
    logger.log('switchURL()', `invoked, url: "${url}"`)
    this.load(url || this._url)
  }

  _bindRTCEvents (pc: RTCPeerConnection) {
    pc.addEventListener('track', this._onTrack)

    pc.addEventListener('connectionstatechange', () => {
      logger.warn('onconnectionstatechange:', pc.connectionState)

      this.emit(EXTEND_EVENTS.RTC_STATE_CHANGE, {
        state: pc.connectionState,
        url: this._url
      })

      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        this.emit('waiting')
        this._stopRtcReport()
      }

      if (pc.connectionState === 'failed') {
        if (this._retry === 0) {
          this._emitError(StreamingError.network(new Error('rtc connect failed')))
          return
        }
        this._retry--
        this.load(this._url)
      }

      if (pc.connectionState === 'connected') {
        this.emit(EVENT.TTFB, {
          url: this._url,
          responseUrl: this._url,
          elapsed: Date.now() - this._rctConnectStartTs
        })
        this._checkStatsError()
        this._startRtcReport()
      }
    })
  }

  _onTrack = (e: RTCTrackEvent) => {
    logger.log('addTrack: ', e.track, e.streams)

    if (e.track.kind === 'audio') {
      this._audio = e.track
    } else {
      this._video = e.track
    }

    if (!this._mediaStream && this._media) {
      this._mediaStream = new MediaStream()
      this._media.srcObject = this._mediaStream
      const req = this._media.play()
      if (req && req.catch) {
        req.catch(e => {})
      }
    }
    this._mediaStream?.addTrack(e.track)
  }

  _checkStatsError = async () => {
    if (this._checkStatsErrorTimer) {
      clearTimeout(this._checkStatsErrorTimer)
    }
    this._checkStatsErrorTimer = setTimeout(async () => {
      const stats = await this.getStatsSnapshoot()
      if (stats.video) {
        const {framesDecoded, bytesReceived} = stats.video
        if (!bytesReceived) {
          this._emitError(StreamingError.create(ERR.MEDIA, ERR.SUB_TYPES.MEDIA_ERR_DECODE, { message: 'rts receive empty'}))
        } else if (!framesDecoded) {
          this._emitError(StreamingError.create(ERR.MEDIA, ERR.SUB_TYPES.MEDIA_ERR_DECODE, { message: 'rts decode error'}))
        }
      }
    }, this._opts.checkStatsErrorDelay)
  }

  initVideoEncodedTransform () {
    if (!this._videoTransceicer || !this._videoTransceicer.receiver) {
      console.log('no receiver found when trying to bind encodedTransform')
      return
    }
    const { receiver } = this._videoTransceicer
    if (isLegacyEncodedTransformSupported()) {
      const { readable, writable } = (receiver as any).createEncodedStreams()
      readable
        .pipeThrough(
          receiveSEITransform((sei) => {
            this.emit(EVENT.SEI, sei)
          })
        )
        .pipeTo(writable)
    } else if (isEncodedTransformSupported()) {

      const worker = new ReceivedSEIWorker()
      const channel = new MessageChannel()
      // eslint-disable-next-line no-undef
      this._videoTransceicer.receiver.transform = new RTCRtpScriptTransform(
        worker,
        {
          port: channel.port2
        },
        [channel.port2]
      )
      channel.port1.onmessage = (e) => {
        this.emit(EVENT.SEI, e.data)
      }
    }
  }

  initAudioEncodedTransform () {
    if (!this._audioTransceicer || !this._audioTransceicer.receiver) {
      logger.warn('no receiver found when trying to bind encodedTransform')
      return
    }
    if (!isLegacyEncodedTransformSupported()) {
      logger.warn('legacy EncodedTransform is not supported')
      return
    }
    const { receiver } = this._audioTransceicer
    const { readable, writable } = (receiver as any).createEncodedStreams()
    readable
      .pipeThrough(
        new TransformStream({
          transform: (chunk, controller) => {
            // 此处 chunk 数据长度超过 1000 会导致 Chrome 内核报错，后续音频无声
            // https://bugs.chromium.org/p/chromium/issues/detail?id=1248479
            if (chunk.data.byteLength <= 1000) {
              controller.enqueue(chunk)
            } else {
              logger.log('too large audio frame', chunk.data.byteLength)
            }
          }
        })
      )
      .pipeTo(writable)
  }

  /**
   *  timeupdate模拟waiting事件
   *  延迟获取pc.getStats()拿到音视频编码信息、metadata
   */
  _bindMediaEvent () {
    this._media?.addEventListener('timeupdate', this._mockWaitingByTimeupdate)
    this._media?.addEventListener('loadeddata', this._onLoadedData)
  }

  _mockWaitingByTimeupdate = () => {
    if (
      !(
        this._pc?.connectionState === 'connected' ||
        this._pc?.iceConnectionState === 'connected'
      )
    )
      return

    clearTimeout(this._waitingTimer)

    this._waitingTimer = setTimeout(() => {
      if (this._media?.paused) return
      this.emit('waiting')
    }, this._opts.stallInterval)
  }

  _onLoadedData = () => {
    logger.log('loadeddata emit')
    this.getStatsSnapshoot().then(snap => {
      this.emit(EVENT.METADATA_PARSED, {
        type: 'video',
        elapsed: Date.now() - this._rctConnectStartTs,
        meta: snap.video
      })

      this.emit(EVENT.METADATA_PARSED, {
        type: 'audio',
        elapsed: Date.now() - this._rctConnectStartTs,
        meta: snap.audio
      })
    })
  }

  /** 用户设置的目标延迟 */
  private _targetDelayHint: number | null = null

  private _setDelayTimer?: number

  /** 设置JittrBuffer的错误信息 */
  private _setDelayError: string | null = null

  /**
   * 逐步设置delay，每次至多设置200ms，直到设置到目标值
   * @param {number} delayHint - 目标延迟，单位为秒
   */
  private _startSetDelayLoop = async (delayHint: number | null) => {
    const key = [
      'playoutDelayHint', // chrome
      'jitterBufferDelayHint', // chrome 80 版本以前
      'jitterBufferTarget' // firefox，最大 4s
    ].find(
      key => key in (this._audioTransceicer?.receiver || {}) && key in (this._videoTransceicer?.receiver || {})
    )
    // 1. 如果不支持设置目标延迟，直接退出
    if (!key) return

    delayHint = (typeof delayHint === 'number' && delayHint > 0) ? delayHint : null
    // 2. 如果当前的目标延迟和设置的目标延迟一致，则退出
    if (this._targetDelayHint === delayHint) return
    logger.log('_startSetDelayLoop()', `invoked, delayHint: ${delayHint}`)

    // 3. 如果已经有设置延迟的定时器，则设置 this._targetDelayHint 后返回
    this._targetDelayHint = delayHint
    if (this._setDelayTimer) return

    // 4. 开始渐进性设置JitterBuffer
    let preAudioStats: IRecvAudioStats | null = null
    let preVideoStats: IRecvVideoStats | null = null
    const { loopInterval, maxAvSyncDelay, maxJitterBufferDiff, maxStep } = this._opts.adaptiveJitterBuffer!
    // 音视频差距过大计数
    let avDiffTooMuchCount = 0

    // 以下为循环设置JitterBuffer逻辑
    this._setDelayTimer = setInterval(async () => {
      try {
        // 1. 如果transceiver不存在，则退出
        if (!this._audioTransceicer?.receiver || !this._videoTransceicer?.receiver) {
          preAudioStats = null
          preVideoStats = null
          return
        }
        /** peer当前的目标JitterBuffer延迟 */
        // @ts-expect-error 这个 key property 一定存在
        const currentTargetDelay = this._audioTransceicer.receiver[key]
        // 2. 如果当前的目标延迟和设置的目标延迟一致，则退出
        // logger.log(`currentTargetDelay: ${currentTargetDelay}, targetDelayHint: ${this._targetDelayHint}`)
        if (currentTargetDelay === this._targetDelayHint) return

        const targetDelayMs = (this._targetDelayHint || 0) * 1000
        const currentTargetDelayMs = currentTargetDelay * 1000
        const [curAudioStats, curVideoStats] = await Promise.all([
          getRecvAudioStats(this._audioTransceicer.receiver.getStats()),
          getRecvVideoStats(this._videoTransceicer.receiver.getStats())
        ])
        if (preAudioStats && preVideoStats) {
          const audioStats = diffRecvAudioStats(preAudioStats, curAudioStats)
          const videoStats = diffRecvVideoStats(preVideoStats, curVideoStats)
          /** 视频同步差 */
          let av_sync_diff
          if (videoStats.estimatedPlayoutTimestamp && audioStats.estimatedPlayoutTimestamp) {
            av_sync_diff = videoStats.estimatedPlayoutTimestamp - audioStats.estimatedPlayoutTimestamp
          }
          /** 当前的视频JitterBuffer延迟 */
          const currentAudioDelay = audioStats.jitterBufferDelay
          /** 当前的视频JitterBuffer延迟 */
          const currentVideoDelay = videoStats.jitterBufferDelay
          if (currentAudioDelay && currentVideoDelay) {
            if (
              Math.abs(currentAudioDelay - currentVideoDelay) > maxAvSyncDelay ||
              (av_sync_diff && Math.abs(av_sync_diff) > maxAvSyncDelay)
            ) {
              // 测试下来，如果突发大的丢包+抖动，浏览器会可能让音频的jb持续很低 100ms，但视频jb持续高 1000ms，动态JB可能永远无法设置。
              // 所以这里加一个最大限制，如果持续8次以上的视频jb远远大于音频jb，则强制将音频jb设置为视频jb
              if (avDiffTooMuchCount++ > 8 && currentVideoDelay > currentAudioDelay) {
                // @ts-expect-error 这个 key property 一定存在
                this._audioTransceicer.receiver[key] = Math.min(currentVideoDelay / 1000, (this._targetDelayHint || 0))
                avDiffTooMuchCount = 0
              }
              // 3. 如果音频和视频的jitterBufferDelay差异过大，说明音频和视频的延迟不同步，跳过本次设置
              logger.log(`av_jb_diff(${currentAudioDelay - currentVideoDelay}=${currentAudioDelay}-${currentVideoDelay}) or av_sync_diff(${av_sync_diff}) too much, skip setting`)
              return
            }
            avDiffTooMuchCount = 0
            if (
              currentTargetDelay !== null &&
              (Math.abs(currentAudioDelay - currentTargetDelayMs) > maxJitterBufferDiff ||
                Math.abs(currentVideoDelay - currentTargetDelayMs) > maxJitterBufferDiff)
            ) {
              // 4. 如果音视频的jitterBufferDelay与目标值的差异大于200ms，说明音频还没有稳定下来，跳过本次设置
              logger.log(`audio(${currentAudioDelay - currentTargetDelayMs}),video(${currentVideoDelay - currentTargetDelayMs}). current_target_diff too much, skip setting`)
              return
            }
            const addOrReduce = targetDelayMs > (currentTargetDelayMs || currentAudioDelay) ? 1 : -1
            // 5. 设置JitterBuffer。下一次设置的延迟，靠近目标延迟，但不超过300ms；如果值足够小(200ms)，则直接设置为null
            let nextJitterBufferDelay: number | null = (Math.abs(targetDelayMs - (currentTargetDelayMs || currentAudioDelay)) > maxStep
              ? (currentTargetDelayMs || currentAudioDelay) + addOrReduce * maxStep
              : targetDelayMs) / 1000
            nextJitterBufferDelay = (nextJitterBufferDelay > 0.2) ? nextJitterBufferDelay : null
            logger.log(`set receiver.${key} = ${nextJitterBufferDelay}`)
            // 实际测试下来，视频delay变小时会有卡顿，所以这里只在增量时设置视频delay
            // this._videoTransceicer.receiver[key] = addOrReduce > 0 ? nextJitterBufferDelay : null
            // @ts-expect-error 这个 key property 一定存在
            this._audioTransceicer.receiver[key] = nextJitterBufferDelay
          }
        }

        preAudioStats = curAudioStats
        preVideoStats = curVideoStats
      } catch (e: any) {
        logger.error('set delay loop error:', e)
        this._setDelayError = e.message || e.toString() || 'unknown error'
      }
    }, loopInterval)
  }

  _getDelay =() => {
    try {
      const key = ['playoutDelayHint', 'jitterBufferDelayHint', 'jitterBufferTarget'].find(key =>
        key in (this._videoTransceicer?.receiver || {}) && key in (this._audioTransceicer?.receiver || {})
      )
      if (!key) return
      // @ts-expect-error 这个 key property 一定存在
      return this._audioTransceicer?.receiver[key]
    } catch (error) {
      logger.error('get delay error')
    }
  }

  private async _getAnswerSdp (pc: RTCPeerConnection) {
    logger.log('_getAnswerSdp()')
    let offer: RTCSessionDescriptionInit | undefined

    let answerSdp: string | undefined

    if (pc.addTransceiver) {
      this._audioTransceicer = pc.addTransceiver('audio', { direction: 'recvonly' })
      this._videoTransceicer = pc.addTransceiver('video', { direction: 'recvonly' })

      this._startSetDelayLoop(this._opts.delayHint ?? 0)

      if (this._opts.enableSei) {
        this.initVideoEncodedTransform()
        this.initAudioEncodedTransform()
      }

      offer = await pc.createOffer()
    } else {
      offer = await pc.createOffer({
        iceRestart: true,
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      })
    }

    logger.log('local offer', offer)

    if (!offer.sdp) {
      throw new Error('create offer failed')
    }

    const parsed = SdpTransform.parse(offer.sdp)

    parsed.media
      .filter(x => x.type === 'audio')
      .forEach(x => {
        const opusPayload = x.rtp.filter(rtp => rtp.codec === 'opus')[0]?.payload
        if (!opusPayload) return

        x.fmtp.forEach(fmtp => {
          if (fmtp.payload !== opusPayload) return
          fmtp.config = 'minptime=10;stereo=1;useinbandfec=1'
        })
      })

    parsed.media
      .filter(x => x.type === 'video')
      .forEach(x => {
        x.fmtp.forEach(x => {
          if (x.config.indexOf('profile-level-id') !== -1) {
            x.config = `${x.config};sps-pps-idr-in-keyframe=1`
          }
        })
      })

    offer.sdp = SdpTransform.write(parsed)

    logger.log('local offer modified:\n', offer)

    await pc.setLocalDescription(offer)

    try {
      const parseSession = /_?session_id=([^&$]+)/.exec(this._url)
      const sessionId = parseSession ? parseSession[1] : Date.now() + ''

      const finnalUrl = addUrlParams(this._url, this._opts.urlParams)

      this._loader && (this._loader.finnalUrl = finnalUrl)

      this._firstFrameObserver.changeFirstFrameState(RTC_FIRST_FRAME_STATE.OFFER_SDP_READY)
      this.emit(EVENT.LOAD_START, { url: finnalUrl })
      this._rctConnectStartTs = Date.now()

      const res = await this._loader?.load(finnalUrl, {
        body: JSON.stringify({
          sessionId,
          version: '1.0-html',
          localSdp: offer
        })
      })

      this.emit(EVENT.LOAD_RESPONSE_HEADERS, { headers: res.response.headers })

      if (res?.data?.code !== 200) {
        throw new Error(`code: ${res?.data?.code}, message:${res?.data?.message}`)
      }
      answerSdp = res.data.remoteSdp.sdp
    } catch (error) {
      this._emitError(StreamingError.network(error))
    }
    return answerSdp
  }

  private async _handleAnswerSdp (pc: RTCPeerConnection, answerSdp?: string) {
    logger.log('_handleAnswerSdp()', `invoked, answerSdp:`, { answerSdp })
    if (!answerSdp) return

    this._firstFrameObserver.changeFirstFrameState(RTC_FIRST_FRAME_STATE.ANSWER_SDP_RECV)

    const parsed = SdpTransform.parse(answerSdp)

    parsed.media.forEach(x => {
      if (x.fingerprint) {
        x.fingerprint.hash = x.fingerprint.hash.toUpperCase()
      }
      // 兼容音画不同步
      const msid = x.ssrcs?.find((s) => s.attribute === 'msid')?.value
      if (msid && !x.msid) {
        x.msid = msid
      }
    })

    const answer = SdpTransform.write(parsed)

    logger.log('answer modified:\n', { answer })

    await pc.setRemoteDescription({
      type: 'answer',
      sdp: answer
    })
    this._firstFrameObserver.changeFirstFrameState(RTC_FIRST_FRAME_STATE.ANSWER_SDP_READY)

    this._firstFrameObserver.bindEvents(pc, this._videoTransceicer, this._audioTransceicer, this._media)
  }

  _startRtcReport () {
    this._stopRtcReport()
    const report = async () => {
      if (!this._audioTransceicer?.receiver || !this._videoTransceicer?.receiver) {
        this._preAudioStats = null
        this._preVideoStats = null
        return
      }
      // 1. 初始化
      if (!this._audioStallObserver && this._audioTransceicer) {
        this._audioStallObserver = new AudioStallObserver()
        this._audioStallObserver.start(this._audioTransceicer)
      }
      if (!this._videoStallObserver && this._video && this._media) {
        this._videoStallObserver = new VideoStallObserver()
        this._videoStallObserver.start(this._media)
      }
      if (this._pc && !this._networkQuality && this._opts.enableNetworkQuality &&  this._opts.networkQuality) {
        this._networkQuality = new NetworkQuality(this._pc, this._opts.networkQuality)
      }
      // 2. 获取 RTC stats
      const [curAudioStats, curVideoStats] = await Promise.all([
        getRecvAudioStats(this._audioTransceicer.receiver.getStats()),
        getRecvVideoStats(this._videoTransceicer.receiver.getStats())
      ])
      // 3. 计算两次 stats 的差异
      if (this._preAudioStats && this._preVideoStats) {
        const audioStats = diffRecvAudioStats(this._preAudioStats, curAudioStats)
        const videoStats = diffRecvVideoStats(this._preVideoStats, curVideoStats)
        this._videoJitterBufferDelay = videoStats.jitterBufferDelay
        // 4. 计算卡顿率
        const audioStallInfo = await this._audioStallObserver?.getAudioStallInfo()
        const videoStallInfo = await this._videoStallObserver?.getStallInfo({
          interval: videoStats.statsInterval,
          frameRateReceived: videoStats.receivedFrameRate,
          frameRateDecoded: videoStats.decoderOutputFrameRate,
          bitrate: videoStats.receivedKBitrate
        })
        // 5. 计算网络质量
        const [quality, qualityInfo] = this._networkQuality?.getDownlinkQuality({
          ...audioStats,
          stallDuration: audioStallInfo?.stallDuration || 0
        }, {
          ...videoStats,
          stallDuration: videoStallInfo?.stall.stallDuration100ms || videoStallInfo?.stall.stallDuration500ms || 0
        }) ?? []
        // 7. 动态调整 buffer
        if (this._opts.enableAdaptiveJitterBuffer) {
          this._handleJitterBuffer(qualityInfo?.qualityQos, this._opts.adaptiveJitterBuffer, this._opts.networkStrategy)
        }

        // 计算音视频同步差
        let av_sync_diff
        if (videoStats.estimatedPlayoutTimestamp && audioStats.estimatedPlayoutTimestamp) {
          av_sync_diff =
            videoStats.estimatedPlayoutTimestamp - audioStats.estimatedPlayoutTimestamp
        }

        // 6. 上报
        this.emit(EXTEND_EVENTS.RTC_NETWORK, {
          interval: videoStats.statsInterval,
          network_quality: quality,
          audio_stall_duration: audioStallInfo?.stallDuration,
          audio_stall_count: audioStallInfo?.stallCount,
          audio_stall_time_per_100sec: ((audioStallInfo?.stallDuration || 0) / audioStats.statsInterval) * 100,
          audio_stall_count_per_100sec: ((audioStallInfo?.stallCount || 0) / audioStats.statsInterval) * 100 * 1000,
          video_stall_duration: videoStallInfo?.stall.stallDuration500ms,
          video_stall_count: videoStallInfo?.stall.stallCount500ms,
          video_stall_duration_100ms: videoStallInfo?.stall.stallDuration100ms,
          video_stall_count_100ms: videoStallInfo?.stall.stallCount100ms,
          video_stall_time_per_100sec: ((videoStallInfo?.stall.stallDuration500ms || 0) / videoStats.statsInterval) * 100,
          video_stall_count_per_100sec: ((videoStallInfo?.stall.stallCount500ms || 0) / videoStats.statsInterval) * 100 * 1000,
          rtt: videoStats.rtt,
          video_jb_delay: videoStats.jitterBufferDelay,
          audio_jb_delay: audioStats.jitterBufferDelay,
          audio_lost_rate: audioStats.audioLossRate,
          video_lost_rate: videoStats.videoLossRate,
          video_retransmitted_rate: videoStats.retransmittedRate,
          quality_audio_level: qualityInfo?.audioLevel,
          quality_video_level: qualityInfo?.videoLevel,
          quality_audio_qos: qualityInfo?.audioQos,
          quality_video_qos: qualityInfo?.videoQos,
          quality_qos: qualityInfo?.qualityQos,
          quality_audio_qoe: qualityInfo?.audioQoe,
          quality_video_qoe: qualityInfo?.videoQoe,
          current_delay: this._getDelay(),
          visibility_state: document.visibilityState,
          target_delay: this._targetDelayHint,
          av_sync_diff,
          set_delay_error: this._setDelayError || null,
          video_width: this._media?.videoWidth || 0,
          video_height: this._media?.videoHeight || 0,
          abr_state: videoStats.abrState || 'none',
        })
      }
      this._preAudioStats = curAudioStats
      this._preVideoStats = curVideoStats
      this._setDelayError = null
    };
    this._rtcReportTimer = setInterval(() => report(), 2000)
    report();
  }

  private _handleJitterBuffer (quality: number | undefined, adaptiveJitterBuffer?: IAdaptiveJitterBufferOption, networkStrategy?: INetworkStrategy) {
    if (!adaptiveJitterBuffer || !networkStrategy) {
      return
    }

    // 滑动窗口
    this._networkQualityResult.push(quality)
    if (this._networkQualityResult.length > adaptiveJitterBuffer.networkWindow) {
      this._networkQualityResult.shift()
    }
    // 过滤空值
    const validNetworkQualityResult = this._networkQualityResult.filter(v => typeof v === 'number')

    if (validNetworkQualityResult.length === adaptiveJitterBuffer.networkWindow) {
      let averageQuality
      if (adaptiveJitterBuffer.windowMethod === 'min') {
        averageQuality = Math.min(...validNetworkQualityResult)
      } else if (adaptiveJitterBuffer.windowMethod === 'max') {
        averageQuality = Math.max(...validNetworkQualityResult)
      } else {
        // 计算中值
        const sorted = [...validNetworkQualityResult].sort((a, b) => a - b)
        const mid = Math.floor(sorted.length / 2)
        averageQuality = sorted.length % 2 !== 0
          ? sorted[mid]
          : (sorted[mid - 1] + sorted[mid]) / 2
      }
      // 调整 jitterBuffer
      if (averageQuality && networkStrategy[averageQuality]?.jitterBuffer !== undefined) {
        this._startSetDelayLoop(networkStrategy[averageQuality]?.jitterBuffer)
      }
    }
  }

  private _stopRtcReport () {
    this._audioStallObserver?.destroy()
    this._videoStallObserver?.destroy()
    this._networkQuality?.destroy()
    delete this._audioStallObserver
    delete this._videoStallObserver
    delete this._networkQuality
    this._preAudioStats = null
    this._preVideoStats = null
    if (this._rtcReportTimer) {
      clearInterval(this._rtcReportTimer)
      this._rtcReportTimer = 0
    }
  }

  private _onLoaderRetry = (error: any, retryTime: number) => {
    this.emit(EVENT.LOAD_RETRY, {
      error: StreamingError.network(error),
      retryTime
    })
  }

  private _clearDelay () {
    if (this._setDelayTimer) {
      clearInterval(this._setDelayTimer)
      delete this._setDelayTimer
    }
    this._targetDelayHint = null
    this._setDelayError = null
  }

  private _disconnect () {
    this._clearDelay()
    this._stopRtcReport()
    clearTimeout(this._checkStatsErrorTimer)
    this._firstFrameObserver.reset()
    this._audioTransceicer = null
    this._videoTransceicer = null
    this._mediaStream = null
  }

  private _emitError (error: any) {
    this._clearDelay()
    this._firstFrameObserver.setFailureReason(error?.originError?.message || error?.originError || error)
    this._firstFrameObserver.reset()
    clearTimeout(this._checkStatsErrorTimer)
    this.emit(EVENT.ERROR, error)
  }

  private _emitFirstFrameState (info: FirstFrameStateResult, extra: FirstFrameObserverExtraInfo) {
    this.emit(EXTEND_EVENTS.RTC_FIRST_FRAME, {
      ...info,
      ...extra,
      initOptionsIncludePreload: !!this._opts.preloadPromise
    })
  }

  public destroy () {
    this._disconnect()
    this._media?.removeEventListener('timeupdate', this._mockWaitingByTimeupdate)
    if (this._media) {
      this._media.srcObject = null
    }
    if (this._pc) {
      this._pc.close()
    }
    this._loader = null
    this._media = null
    this._pc = null
  }
}

try {
  if (localStorage.getItem('xgd')) {
    Rts.enableLogger()
  } else {
    Rts.disableLogger()
  }
} catch (error) {
  // ignore
}
